import React, { useState, useMemo, useCallback } from 'react';
import z from 'zod';
import _ from 'lodash';
import { store, useAppSelector } from 'careand-redux/store';
import {
  ActionStatus,
  ClinicalAction,
  matchClinicalAction,
  actionsActions,
  ActionType,
  AdditionalConsiderationAction,
  ContactPayload,
} from 'careand-redux/slices/actions';
import Icon from 'components/Utils/NewIcon';
import './ActionPanel.scss';
import { UnwrappedAppDispatch, useAppDispatch } from '../../careand-redux/useAppDispatch';
import { uiActions } from '../../careand-redux/slices/ui/ui';
import DocumentTemplateUtils from '../../services/DocumentTemplateUtils';
import { IconButton } from '@material-ui/core';
import { useIsLoading } from '../../careand-redux/slices/ui/hooks';
import { api } from 'careand-redux/api/ApiThunks';
import type {
  Appointment,
  DocumentTemplatePrefilled,
  Patient,
  PhysicianContact,
  ReferralRequest,
} from '../../generated/api';
import { useCPPContext } from '../Patient/CPP/CPPContext';
import CPPUtils from '../../services/CPPUtils';
import moment from 'moment-timezone';
import useAssistant from '../../hooks/useAssistant';
import MiscUtils from '../../services/MiscUtils';
import AutoHeightCollapse from '../Common/AutoHeightCollapse';

interface ActionPanelProps {
  appointmentId: string;
  getSuggestions: (instructions?: string) => void;
}

type ActionGroup<T extends ActionType> = {
  type: T;
  actions: (T extends 'additional-consideration'
    ? AdditionalConsiderationAction
    : ClinicalAction<T>)[];
};

function isGroup(action: ActionGroup<any> | ClinicalAction<any>): action is ActionGroup<any> {
  return 'actions' in action;
}

const AdditionalConsiderationsGroupCard = React.memo(
  ({
    group,
    onChange,
  }: {
    group: ActionGroup<'additional-consideration'>;
    onChange: (action: AdditionalConsiderationAction, status: ActionStatus) => void;
  }) => {
    const AdditionalConsiderationItem = useCallback(
      ({ action }: { action: AdditionalConsiderationAction }) => {
        return (
          <div
            key={action.id}
            className="additional-consideration-item"
            data-action-status={action.status}
          >
            <input
              type="checkbox"
              checked={action.status === 'done'}
              onChange={(ev) => {
                onChange(action, ev.target.checked ? 'done' : 'pending-approval');
              }}
            />
            <div className="flex gap-1 w-full items-center">
              <div className="grow">{action.description}</div>
              <Icon src="circle-info" alt={action.consideration} showTooltip />
              {/*
                    TODO LATER
                    <ContextMenuTrigger id={action.id} holdToDisplay={0.00001}>
                    <Icon src="ellipsis" />
                    </ContextMenuTrigger>

                    <ContextMenu id={action.id} className="!z-50">
                    <MenuItem onClick={() => alert('todo')}>Create Task</MenuItem>
                    <MenuItem onClick={() => alert('todo')}>Create Reminder</MenuItem>
                    </ContextMenu>
                  */}
            </div>
          </div>
        );
      },
      [],
    );

    const isPending = useCallback((action: AdditionalConsiderationAction) => {
      return action.status !== 'done' && action.status !== 'rejected' && action.status !== 'error';
    }, []);

    return (
      <div className="action-item">
        <div className="action-item-header">
          <div className="action-type-icon">
            <Icon src="messages" width="18px" height="18px" />
          </div>
          <div className="action-description">Additional Considerations</div>
        </div>
        <div className="action-content">
          <div className="action-prescriptions-list">
            {group.actions.filter(isPending).map((action) => (
              <AdditionalConsiderationItem action={action} />
            ))}
          </div>
          <AutoHeightCollapse
            header={(open, toggle) => (
              <div className="flex items-center" onClick={toggle}>
                <div className="small-title">Completed</div>
                <Icon src={open ? 'chevron-up' : 'chevron-down'} />
              </div>
            )}
            body={
              <div className="action-prescriptions-list">
                {group.actions
                  .filter((it) => !isPending(it))
                  .map((action) => (
                    <AdditionalConsiderationItem action={action} />
                  ))}
              </div>
            }
          />
        </div>
      </div>
    );
  },
);

export function ActionPanel({ appointmentId, getSuggestions }: ActionPanelProps) {
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(
    api.AI.runSavedPrompt,
    (params) =>
      params?.name === 'Appointment Suggestions' ||
      // legacy fallback prompts (pre-merge)
      params?.name === 'Appointment Action Suggestions' ||
      params?.name === 'Appointment Additional Considerations',
  );
  const appointment = useAppSelector(
    (state) => state.ui.bubbles.find((it) => it.appointment_id === appointmentId)?.appointment,
  );
  const patient = useAppSelector((state) => state.patients.byId[appointment!.patient_id!]);

  if (!appointment || !patient) {
    throw new Error('Tried to render ActionPanel without appointment or patient');
  }

  const patientId = patient.id!;
  const physicianId = appointment.physician_id!;

  // TODO refactor
  const _allActions = useAppSelector((state) => state.actions.byId);
  const allActions = useMemo(() => {
    return _(_allActions)
      .values()
      .filter((it) => it.appointment_id === appointmentId)
      .value();
  }, [_allActions, appointmentId]);

  const processedActions = useMemo(() => {
    // For prescriptions, include rejected ones (they'll be shown with strikethrough at bottom)
    // For other action types, filter out rejected ones
    const prescriptionActions = allActions.filter((it): it is ClinicalAction<'prescription'> =>
      matchClinicalAction(it, 'prescription'),
    );
    const additionalConsiderations = allActions.filter((it): it is AdditionalConsiderationAction =>
      matchClinicalAction(it, 'additional-consideration'),
    );

    const otherActions = allActions.filter(
      (it) =>
        it.type !== 'prescription' &&
        it.status !== 'rejected' &&
        it.type !== 'additional-consideration',
    );

    let prescriptionGroup: ActionGroup<'prescription'> | undefined = undefined;
    if (prescriptionActions.length > 0) {
      // Sort prescription actions: rejected ones go to the bottom
      const sortedPrescriptions = _.sortBy(prescriptionActions, (action) => {
        return action.status === 'rejected' ? 1 : 0;
      });

      prescriptionGroup = {
        type: 'prescription',
        actions: sortedPrescriptions,
      };
    }

    let additionalConsiderationsGroup: ActionGroup<'additional-consideration'> | undefined =
      undefined;
    if (additionalConsiderations.length > 0) {
      additionalConsiderationsGroup = {
        type: 'additional-consideration',
        actions: additionalConsiderations,
      };
    }

    let otherDone = otherActions.filter((it) => it.status === 'done');
    let otherPending = otherActions.filter((it) => it.status !== 'done');

    return _.compact([
      prescriptionGroup,
      additionalConsiderationsGroup,
      ...otherPending,
      ...otherDone,
    ]);
  }, [allActions]);

  function onActionSelected(action: ClinicalAction<any>) {
    if (matchClinicalAction(action, 'referral', 'requisition', 'other_document')) {
      //Handled elsewhere
    } else if (matchClinicalAction(action, 'prescription')) {
      dispatch(
        uiActions.addPinnedDocument({
          type: 'prescription',
          patientId,
          prescription: {
            medications: action.execution_data?.medication
              ? [action.execution_data?.medication]
              : [],
            patient_id: patientId,
          },
        }),
      );
    }
  }

  return (
    <div className="action-panel">
      <form
        className="action-panel-header flex items-center gap-2"
        onSubmit={(ev) => {
          ev.preventDefault();
          const input = ev.currentTarget.elements.namedItem(
            'carey-suggestions-input',
          ) as HTMLInputElement;
          getSuggestions(input.value);
          input.value = '';
        }}
      >
        <input
          id="carey-suggestions-input"
          type="text"
          placeholder="Carey Suggestions"
          style={{ flexGrow: 1 }}
        />
        {isLoading && <Icon loading width={17} height={17} />}
        <IconButton size="small" onClick={() => dispatch(actionsActions.clearAll())}>
          <Icon src="trash" />
        </IconButton>
        <IconButton type="submit" size="small">
          <Icon src="message" />
        </IconButton>
      </form>

      <div className="action-list no-scrollbar">
        {processedActions.length === 0 && (
          <div className="action-empty">
            No suggestions yet. Carey will analyze the appointment every few minutes.
          </div>
        )}

        {processedActions.map((item) => {
          if (isGroup(item) && item.type === 'prescription') {
            return (
              <PrescriptionGroupCard
                patientId={patientId}
                key="prescription-group"
                group={item}
                onActionSelected={onActionSelected}
              />
            );
          }

          if (isGroup(item) && item.type === 'additional-consideration') {
            return (
              <AdditionalConsiderationsGroupCard
                key="additional-considerations-group"
                group={item}
                onChange={(action, status) => {
                  dispatch(actionsActions.updateAction({ id: action.id, status }));
                }}
              />
            );
          }

          return (
            <div
              key={item.id}
              data-action-status={item.status}
              className="action-item"
              onClick={() => onActionSelected(item)}
            >
              <div className="action-item-header">
                <div className="action-type-icon">
                  <Icon
                    src={getActionIcon(item.type)}
                    width="18px"
                    height="18px"
                    loading={item.status === 'in-progress'}
                  />
                </div>
                <div className="action-description">{item.description}</div>
              </div>
              <div className="action-content">
                {matchClinicalAction(item!, 'referral', 'requisition', 'other_document') && (
                  <>
                    <div className="template-suggestion-list">
                      {(item.execution_data?.templates?.length || 0) > 0 && (
                        <>
                          <span className="subaction-title">Templates</span>
                          <div className="template-suggestion-list">
                            {item.execution_data?.templates?.map((template) => (
                              <TemplateBadge
                                key={template.id}
                                id={template.id}
                                actionId={item.id}
                                appointment={appointment}
                                patientId={patientId}
                                prefilledValues={template.fields}
                              />
                            ))}
                          </div>
                        </>
                      )}
                      {((matchClinicalAction(item, 'referral') &&
                        item.execution_data?.contacts?.length) ||
                        0) > 0 && (
                        <>
                          <span className="subaction-title">Contacts</span>
                          {matchClinicalAction(item!, 'referral') &&
                            item.execution_data?.contacts.map((contactPayload) => {
                              return (
                                <PhysicianContactBadge
                                  actionId={item.id}
                                  patient={patient}
                                  physicianId={physicianId}
                                  contact={contactPayload}
                                />
                              );
                            })}
                        </>
                      )}
                    </div>
                    {matchClinicalAction(item!, 'requisition') &&
                      item.execution_data?.prefilledTemplates &&
                      item.execution_data.prefilledTemplates.length > 0 && (
                        <>
                          <span className="subaction-title">Prefilled Templates</span>
                          <div className="template-suggestion-list">
                            <PrefilledTemplateBadges
                              prefilledTemplates={item.execution_data.prefilledTemplates}
                              patientId={patientId}
                            />
                          </div>
                        </>
                      )}
                  </>
                )}
                {item.type === 'referral' && (
                  <>
                    <div
                      className="expanding-button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        openSideReferral(dispatch, patientId, physicianId, null);
                      }}
                    >
                      <Icon src="plus" width="14px" height="14px" />
                      New Long Referral
                    </div>
                    <div
                      className="expanding-button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        openSideNewReferralRequest(dispatch, patientId, physicianId, {});
                      }}
                    >
                      <Icon src="plus" width="14px" height="14px" />
                      New Referral Request
                    </div>
                  </>
                )}
                {item.status === 'in-progress' && item.executor_message && (
                  <div className="action-executor-message">{item.executor_message}</div>
                )}
                <div className="action-card-buttons bg-white">
                  {item.reason && (
                    <Icon
                      className="reason-icon"
                      src="lightbulb"
                      width="14px"
                      height="14px"
                      alt={item.reason}
                      showTooltip
                    />
                  )}
                  <Icon
                    className="reject-icon"
                    src="trash"
                    width="14px"
                    height="14px"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(
                        actionsActions.updateAction({
                          ...item,
                          status: 'rejected',
                          updated_at: moment().unix(),
                        }),
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PrescriptionGroupCard({
  group,
  onActionSelected,
  patientId,
}: {
  group: ActionGroup<'prescription'>;
  onActionSelected: (action: ClinicalAction<any>) => void;
  patientId: string;
}) {
  const dispatch = useAppDispatch();

  const status = useMemo(() => {
    const statusOrder: ActionStatus[] = [
      'in-progress',
      'pending-approval',
      'ready',
      'done',
      'rejected',
      'error',
    ];
    return _(group.actions).maxBy((it) => statusOrder.indexOf(it.status))!.status;
  }, [group]);

  const handleRejectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    group.actions.forEach((action) => {
      if (action.status !== 'rejected') {
        dispatch(
          actionsActions.updateAction({
            ...action,
            status: 'rejected',
            updated_at: moment().unix(),
          }),
        );
      }
    });
  };

  const prescribeAllMedications = useMemo(() => {
    return group.actions
      .filter((action) => action.status === 'pending-approval' && action.execution_data?.medication)
      .map((action) => action.execution_data!.medication!);
  }, [group]);

  const handlePrescribeAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Collect all medications from pending-approval actions

    if (prescribeAllMedications.length > 0) {
      dispatch(
        uiActions.addPinnedDocument({
          type: 'prescription',
          patientId,
          prescription: {
            medications: prescribeAllMedications,
            patient_id: patientId,
          },
        }),
      );
    }
  };

  return (
    <div className={`action-item action-${status}`}>
      <div className="action-item-header">
        <div className="action-type-icon">
          <Icon src="prescription" width="18px" height="18px" loading={status === 'in-progress'} />
        </div>
        <div className="action-description">Prescriptions</div>
      </div>
      <div className="action-content">
        <div className="action-prescriptions-list">
          {group.actions
            .filter((action) => action.execution_data?.medication)
            .map((action) => (
              <PrescriptionActionRow
                key={action.id}
                action={action}
                onActionSelected={onActionSelected}
              />
            ))}
        </div>
        {prescribeAllMedications.length > 0 && (
          <div className="expanding-button" onClick={handlePrescribeAll}>
            <Icon src="pills" width="14px" height="14px" />
            Prescribe All
          </div>
        )}
      </div>
      {prescribeAllMedications.length > 0 && (
        <div className="action-card-buttons">
          <div className="icon reject-icon clickable" onClick={handleRejectAll}>
            <Icon src="trash" width="14px" height="14px" />
          </div>
        </div>
      )}
    </div>
  );
}

function PrescriptionActionRow({
  action,
  onActionSelected,
}: {
  action: ClinicalAction<'prescription'>;
  onActionSelected: (action: ClinicalAction<any>) => void;
}) {
  const dispatch = useAppDispatch();
  const medication = action.execution_data?.medication;

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      actionsActions.updateAction({
        ...action,
        status: 'rejected',
        updated_at: moment().unix(),
      }),
    );
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      actionsActions.updateAction({
        ...action,
        status: 'pending-approval',
        updated_at: moment().unix(),
      }),
    );
  };

  //console.log(action);

  return (
    <div
      className="action-prescription-item action-prescription-medication"
      data-action-status={action.status}
      onClick={(e) => {
        e.stopPropagation();
        if (medication && action.status !== 'rejected') {
          onActionSelected(action);
        }
      }}
    >
      <span className="medication-text flex">
        • {CPPUtils.getPrescriptionMedicationSummary(medication!) || action.description}
      </span>
      <span className="medication-actions">
        <Icon alt={action.reason} showTooltip src="lightbulb" width="14px" height="14px" />
        {medication && (
          <>
            {action.status !== 'rejected' ? (
              <Icon src="trash" width="14px" height="14px" onClick={handleReject} />
            ) : (
              <Icon src="undo" width="14px" height="14px" onClick={handleUndo} />
            )}
          </>
        )}
      </span>
    </div>
  );
}

function getActionIcon(type: string): string {
  switch (type) {
    case 'prescription':
      return 'prescription';
    case 'referral':
      return 'user-doctor';
    case 'requisition':
      return 'chart-line';
    case 'other_document':
      return 'file-lines';
    default:
      return 'clipboard';
  }
}

function PhysicianContactBadge({
  actionId,
  patient,
  physicianId,
  contact,
}: {
  actionId: string;
  patient: Patient;
  physicianId: string;
  contact: ContactPayload;
}) {
  const dispatch = useAppDispatch();
  const assistant = useAssistant();
  const { unreactiveNoteValue } = useCPPContext();
  const [generating, setGenerating] = useState(false);
  const hasFields = !!contact.further_discussion || !!contact.reason_for_referral;
  const transcription = useAppSelector(
    (state) =>
      state.actions.executorStateByAppointmentId[state.actions.byId[actionId]?.appointment_id ?? '']
        ?.transcription,
  );

  const distance = useMemo(() => {
    const dist = MiscUtils.getDistance(patient, contact.contact);
    if (!dist) return '';
    if (dist > 1000) {
      return ` - ${(dist / 1000.0).toFixed(1)}Km`;
    } else {
      return ` - ${dist.toFixed(1)}m`;
    }
  }, [patient, contact.contact]);

  async function onGenerate() {
    setGenerating(true);
    const result = await assistant
      .runSavedPromptTyped(
        'Fill Long Referral Text',
        {
          appointmentNote: unreactiveNoteValue.current!,
          transcription: transcription ? JSON.stringify(transcription, null, 2) : '',
          contact: JSON.stringify(contact, null, 2),
        },
        {
          patientId: patient.id!,
        },
      )
      .finally(() => {
        setGenerating(false);
      });

    if (!result) {
      return;
    }
    const { reason_for_referral, further_discussion } = result;

    dispatch(
      actionsActions.updateContactFields({
        action_id: actionId,
        contact_id: contact.contact.id!,
        reason_for_referral,
        further_discussion,
      }),
    );
  }

  function onClickCreateReferralRequest() {
    openSideNewReferralRequest(dispatch, patient.id!, physicianId, {
      send_to: contact.contact,
    });
  }

  function onClick() {
    openSideReferral(dispatch, patient.id!, physicianId, contact);
  }

  return (
    <div className="template-suggestion-item" data-has-field={hasFields} onClick={onClick}>
      <span>
        {contact.contact.specialty} - {contact.contact.name}
        {distance}
      </span>
      <Icon
        src="circle-plus"
        alt="Create Referral Request"
        width="16px"
        height="16px"
        showTooltip
        onClick={(ev) => {
          ev.stopPropagation();
          onClickCreateReferralRequest();
        }}
      />
      {!hasFields && (
        <Icon
          src="sparkles"
          alt="AutoFill"
          width="16px"
          height="16px"
          showTooltip
          loading={generating}
          onClick={(ev) => {
            ev.stopPropagation();
            onGenerate();
          }}
        />
      )}
    </div>
  );
}

function TemplateBadge({
  id,
  actionId,
  patientId,
  prefilledValues,
  appointment,
}: {
  id: string;
  actionId: string;
  patientId: string;
  prefilledValues?: { id: string; value: string }[];
  appointment: Appointment;
}) {
  const dispatch = useAppDispatch();
  const { unreactiveNoteValue } = useCPPContext();

  const templates = useAppSelector((state) => state.templates.templates);
  const text = useMemo(() => templates.find((it) => it.id === id)?.description, [templates, id]);
  const hasFields = !!prefilledValues;
  const generatingFields = useIsLoading(api.Templates.fillDocumentTemplate, (args) => {
    return args?.templateId === id;
  });

  async function onGenerateFields() {
    const res = await dispatch(
      api.Templates.fillDocumentTemplate({
        templateId: id,
        fillDocumentTemplateParams: {
          patient_id: patientId,
          appointment,
          appointment_note: unreactiveNoteValue.current ?? '',
        },
      }),
    );
    if (!res) {
      return; //TODO better error handling
    }

    dispatch(
      actionsActions.updateTemplateFields({
        action_id: actionId,
        template_id: id,
        fields: res,
      }),
    );
  }

  function onOpen() {
    dispatch(
      uiActions.addPinnedDocument({
        type: 'template',
        patientId,
        id,
        title: text,
        payload: {
          templateId: id,
          prefilledValues: prefilledValues
            ? DocumentTemplateUtils.convertPrefilledValues(prefilledValues)
            : undefined,
        },
      }),
    );
  }

  return (
    <div className="template-suggestion-item" data-has-field={hasFields} onClick={onOpen}>
      <span>{text}</span>
      {!hasFields && (
        <Icon
          src="sparkles"
          alt="AutoFill"
          width="16px"
          height="16px"
          showTooltip
          loading={generatingFields}
          onClick={(ev) => {
            ev.stopPropagation();
            onGenerateFields();
          }}
        />
      )}
    </div>
  );
}

function PrefilledTemplateBadges({
  prefilledTemplates,
  patientId,
}: {
  prefilledTemplates: DocumentTemplatePrefilled[];
  patientId: string;
}) {
  const dispatch = useAppDispatch();
  const regularTemplates = useAppSelector((state) => state.templates.templates);

  // Group by bundle (copied from PrefilledTemplates component logic)
  const bundled = useMemo(() => {
    const bundles: Array<{ title: string; templates: DocumentTemplatePrefilled[] }> = [];
    _.compact(prefilledTemplates).forEach((template) => {
      const matchingBundle = template.bundle && bundles.find((it) => it.title === template.bundle);
      if (matchingBundle) {
        matchingBundle.templates.push(template);
      } else {
        bundles.push({
          title: template.bundle ?? template.title ?? 'Untitled',
          templates: [template],
        });
      }
    });
    return bundles;
  }, [prefilledTemplates]);

  function onClickTemplate(prefilledTemplate: DocumentTemplatePrefilled) {
    console.log(prefilledTemplate);
    const template = regularTemplates.find((t) => t.id === prefilledTemplate.template_id);
    if (!template) return;

    const prefilledValues = DocumentTemplateUtils.convertPrefilledValues(prefilledTemplate.values);
    console.log(template, prefilledValues);
    dispatch(
      uiActions.addPinnedDocument({
        type: 'template',
        patientId,
        id: prefilledTemplate.id!,
        title: prefilledTemplate.title,
        payload: {
          templateId: template.id,
          prefilledValues,
        },
      }),
    );
  }

  function onClickOpenAllBundle(
    bundle: { title: string; templates: DocumentTemplatePrefilled[] },
    ev: React.MouseEvent,
  ) {
    ev.stopPropagation();
    bundle.templates.forEach((prefilledTemplate, index) => {
      const template = regularTemplates.find((t) => t.id === prefilledTemplate.template_id);
      if (!template) return;

      const prefilledValues = DocumentTemplateUtils.convertPrefilledValues(
        prefilledTemplate.values,
      );

      dispatch(
        uiActions.addPinnedDocument({
          type: 'template',
          patientId,
          id: prefilledTemplate.id! + index,
          title: prefilledTemplate.title,
          payload: {
            templateId: template.id,
            prefilledValues,
          },
        }),
      );
    });
  }

  // Render bundles and templates
  return (
    <>
      {bundled.map((bundle) => {
        const inBundle = bundle.templates.length > 1;

        return (
          <React.Fragment key={bundle.title}>
            {inBundle && (
              <div
                className="template-suggestion-item bundle-header"
                style={{
                  fontWeight: 600,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>
                  {bundle.title} Bundle ({bundle.templates.length})
                </span>
                <Icon
                  showTooltip={true}
                  alt="Open all documents"
                  src="pencil"
                  width="14px"
                  height="14px"
                  onClick={(ev) => onClickOpenAllBundle(bundle, ev)}
                />
              </div>
            )}
            {bundle.templates.map((template) => (
              <div
                key={template.id}
                className="template-suggestion-item"
                data-has-field="true"
                onClick={() => onClickTemplate(template)}
              >
                {template.title}
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}
function openSideReferral(
  dispatch: UnwrappedAppDispatch,
  patientId: string,
  physicianId: string,
  contact: ContactPayload | null,
) {
  const state = store.getState();
  const patient = state.patients.byId[patientId];
  const physician = state.physicians.byId[physicianId];
  const hr = state.healthRecords.byId[patientId]?.healthRecords;

  const text = CPPUtils.getReferralText(
    patient!,
    physician!,
    CPPUtils.getReferralCPPInfo(hr),
    contact ?? undefined,
  );

  dispatch(
    uiActions.addPinnedDocument({
      type: 'referral',
      patientId,
      text,
      send_to: contact?.contact,
    }),
  );
}

function openSideNewReferralRequest(
  dispatch: UnwrappedAppDispatch,
  patientId: string,
  physicianId: string,
  referralRequest?: Partial<ReferralRequest> & { send_to?: PhysicianContact | null },
) {
  const DEFAULT_REQUEST = {
    instructions: '',
    urgent: false,
    hide: false,
    document_ids: [],
    send_to: undefined,
  };

  dispatch(
    uiActions.addPinnedDocument({
      type: 'referral_request',
      patientId,
      referralRequest: {
        ...DEFAULT_REQUEST,
        physician_id: physicianId,
        patient_id: patientId,
        ...((referralRequest ?? {}) as Partial<ReferralRequest>),
      },
    }),
  );
}
